<mat-card>
  <mat-card-content>
    <div class="discover-header">
      <div class="discover-title">
        <h2>Discover Product Usage</h2>
        <p class="discover-subtitle">Find where products are being used across homes and projects</p>
      </div>
      <button mat-stroked-button type="button" class="manual-scan-toggle"
              (click)="toggleManualScan()"
              matTooltip="Manually scan a render's artifact for product usage">
        <mat-icon>fingerprint</mat-icon>
        {{ showManualScan ? 'Hide scan tool' : 'Scan from artifact' }}
      </button>
    </div>

    <!-- Manual scan panel -->
    <div *ngIf="showManualScan" class="search-section manual-scan-panel">
      <form [formGroup]="artifactSearchForm" (ngSubmit)="loadFromArtifact()">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Artifact ID (Rendered Output ID)</mat-label>
          <input matInput formControlName="artifactId" placeholder="Enter artifact ID">
          <mat-icon matSuffix>fingerprint</mat-icon>
          <mat-hint>Enter an artifact ID to ingest product usage data from that render</mat-hint>
        </mat-form-field>
        <div class="button-row">
          <button mat-raised-button color="primary" type="submit" [disabled]="isLoading || artifactSearchForm.invalid">
            <mat-icon>download</mat-icon> Load Product Usage
          </button>
          <button mat-button type="button" (click)="toggleManualScan()" [disabled]="isLoading">
            <mat-icon>close</mat-icon> Cancel
          </button>
        </div>
      </form>
    </div>

    <!-- Search form -->
    <div class="search-section">
      <form [formGroup]="searchForm" (ngSubmit)="searchProductUsage()">
        <div class="search-grid">
          <mat-form-field appearance="outline">
            <mat-label>Manufacturer</mat-label>
            <input matInput formControlName="manufacturer" [matAutocomplete]="manufacturerAuto"
                   placeholder="Any manufacturer">
            <mat-icon matSuffix>business</mat-icon>
            <mat-autocomplete #manufacturerAuto="matAutocomplete">
              <mat-option *ngFor="let m of filteredManufacturers$ | async" [value]="m.name">{{m.name}}</mat-option>
            </mat-autocomplete>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Company</mat-label>
            <input matInput formControlName="companyId" [matAutocomplete]="companyAuto"
                   placeholder="Any company">
            <mat-icon matSuffix>apartment</mat-icon>
            <mat-autocomplete #companyAuto="matAutocomplete" [displayWith]="displayCompany">
              <mat-option *ngFor="let c of filteredCompanies$ | async" [value]="c.id">{{c.name}}</mat-option>
            </mat-autocomplete>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>SKU</mat-label>
            <input matInput formControlName="sku" placeholder="Optional">
            <mat-icon matSuffix>qr_code</mat-icon>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Rendered Output ID</mat-label>
            <input matInput formControlName="renderedOutputId" placeholder="Optional">
            <mat-icon matSuffix>fingerprint</mat-icon>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Product ID</mat-label>
            <input matInput formControlName="productId" placeholder="Optional">
            <mat-icon matSuffix>inventory_2</mat-icon>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Result Limit</mat-label>
            <input matInput type="number" formControlName="limit" placeholder="200">
            <mat-hint>Max records (1-1000)</mat-hint>
          </mat-form-field>
        </div>
        <div class="button-row">
          <button mat-raised-button color="primary" type="submit" [disabled]="isLoading">
            <mat-icon>search</mat-icon> Search
          </button>
          <button mat-button type="button" (click)="clearSearch()" [disabled]="isLoading">
            <mat-icon>clear</mat-icon> Clear
          </button>
        </div>
      </form>
    </div>

    <!-- Loading -->
    <div *ngIf="isLoading" class="loading-container">
      <mat-spinner diameter="50"></mat-spinner>
      <p>Searching...</p>
    </div>

    <!-- Results -->
    <div *ngIf="!isLoading && searchPerformed" class="results-section">
      <div class="results-header">
        <h3>
          <mat-icon>analytics</mat-icon>
          Results
          <span class="result-count" *ngIf="totalResults > 0">({{totalResults}} product{{totalResults !== 1 ? 's' : ''}})</span>
        </h3>
        <div class="header-actions">
          <button mat-button color="accent" (click)="exportResults()" *ngIf="totalResults > 0">
            <mat-icon>download</mat-icon> Export CSV
          </button>
        </div>
      </div>

      <div *ngIf="totalResults === 0" class="no-results">
        <mat-icon>search_off</mat-icon>
        <p>No product usage found</p>
      </div>

      <div *ngIf="totalResults > 0" class="table-container">
        <table mat-table [dataSource]="dataSource" matSort multiTemplateDataRows class="full-width">
          <ng-container matColumnDef="preview">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let record">
              <img *ngIf="getPreviewUrl(record)" class="table-preview"
                   [src]="getPreviewUrl(record)" (error)="$event.target.style.display='none'">
              <div *ngIf="!getPreviewUrl(record)" class="preview-placeholder">
                <mat-icon>inventory_2</mat-icon>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="product">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Product</th>
            <td mat-cell *matCellDef="let record">
              <div class="product-cell">
                <span class="product-primary clickable"
                      *ngIf="record.sku"
                      (click)="searchBySku(record.sku); $event.stopPropagation()"
                      matTooltip="Search by SKU">{{record.sku}}</span>
                <span class="product-primary muted" *ngIf="!record.sku">No SKU</span>
                <span class="product-secondary clickable"
                      *ngIf="record.manufacturer"
                      (click)="searchByManufacturer(record.manufacturer); $event.stopPropagation()"
                      matTooltip="Search by manufacturer">{{record.manufacturer}}</span>
                <span class="product-secondary muted"
                      *ngIf="!record.manufacturer && record.productId">{{record.productId.substring(0, 8)}}…</span>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="homeName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Home</th>
            <td mat-cell *matCellDef="let record">
              <span class="clickable"
                    *ngIf="record.homeName"
                    (click)="searchByHome(record.renderedOutputId); $event.stopPropagation()"
                    matTooltip="Filter by this home">{{record.homeName}}</span>
              <span class="muted" *ngIf="!record.homeName">—</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="companyName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Company</th>
            <td mat-cell *matCellDef="let record">
              <span class="clickable"
                    *ngIf="record.companyId && record.companyName"
                    (click)="searchByCompany(record.companyId); $event.stopPropagation()"
                    matTooltip="Filter by this company">{{record.companyName}}</span>
              <span *ngIf="!record.companyId || !record.companyName">{{record.companyName || '—'}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="sightingCount">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Sightings</th>
            <td mat-cell *matCellDef="let record">
              <span class="stat-number">{{record.sightingCount || 0}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="replacementCount">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Replacements</th>
            <td mat-cell *matCellDef="let record">
              <span class="stat-number">{{record.replacementCount || 0}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="hotspotCount">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Hotspots</th>
            <td mat-cell *matCellDef="let record">
              <span class="stat-number">{{record.hotspotCount || 0}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="impressionCount">
            <th mat-header-cell *matHeaderCellDef mat-sort-header
                [matTooltip]="'Customer impressions in last ' + impressionWindowHours + 'h'">
              Impressions ({{impressionWindowHours}}h)
            </th>
            <td mat-cell *matCellDef="let record">
              <mat-spinner *ngIf="loadingImpressions" diameter="14"></mat-spinner>
              <span *ngIf="!loadingImpressions" class="stat-number"
                    [class.text-muted]="!record.impressionCount">{{record.impressionCount || 0}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="rooms">
            <th mat-header-cell *matHeaderCellDef>Rooms</th>
            <td mat-cell *matCellDef="let record">
              <span class="rooms-cell">{{getRoomsList(record)}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let record" class="actions-cell">
              <button mat-icon-button
                      (click)="viewHome(record); $event.stopPropagation()"
                      *ngIf="record.renderedOutputId"
                      matTooltip="View home">
                <mat-icon>home</mat-icon>
              </button>
              <button mat-icon-button
                      (click)="viewProduct(record); $event.stopPropagation()"
                      *ngIf="record.productId || record.legacyProductId"
                      matTooltip="View product">
                <mat-icon>inventory_2</mat-icon>
              </button>
              <mat-icon class="expand-chevron" [class.expanded]="expandedRecord === record">
                expand_more
              </mat-icon>
            </td>
          </ng-container>

          <!-- Expandable detail row showing sightings -->
          <ng-container matColumnDef="expandedDetail">
            <td mat-cell *matCellDef="let record" [attr.colspan]="displayedColumns.length" class="expanded-cell">
              <div class="sighting-detail" [class.open]="expandedRecord === record">
                <div *ngIf="expandedRecord === record">
                  <div *ngIf="isSightingsLoading(record)" class="sighting-loading">
                    <mat-spinner diameter="24"></mat-spinner>
                    <span>Loading sightings…</span>
                  </div>
                  <div *ngIf="!isSightingsLoading(record) && getSightings(record).length > 0" class="sighting-list">
                    <div class="sighting-row" *ngFor="let s of getSightings(record)">
                      <span class="sighting-room">{{s.roomName}}</span>
                      <span class="sighting-panorama">{{s.panoramaName}}</span>
                      <span class="sighting-distance">{{s.distance | number:'1.1-1'}}m</span>
                      <button mat-icon-button (click)="viewSighting(record, s)" matTooltip="Open in SituVIEW">
                        <mat-icon>open_in_new</mat-icon>
                      </button>
                    </div>
                  </div>
                  <div *ngIf="!isSightingsLoading(record) && getSightings(record).length === 0" class="sighting-empty">
                    No sightings
                  </div>
                </div>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let record; columns: displayedColumns;"
              class="record-row" [class.expanded]="expandedRecord === record"
              (click)="toggleExpand(record)"></tr>
          <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
        </table>

        <mat-paginator [pageSizeOptions]="[10, 25, 50, 100]" [pageSize]="25" showFirstLastButtons></mat-paginator>
      </div>
    </div>
  </mat-card-content>
</mat-card>
